import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
  ClientIcon,
  CompanyIcon,
  DisciplineIcon,
  IndustryIcon,
  PassionIcon,
  PeopleIcon,
  ProjectIcon,
  SkillIcon,
} from 'app/components/DetailsPages/shared/CommonGround/Icons';
import GlobalClientDrawer from 'app/components/Drawers/GlobalClient/GlobalClientDrawer';
import GlobalCompanyDrawer from 'app/components/Drawers/GlobalCompany/GlobalCompanyDrawer';
import GlobalDisciplineDrawer from 'app/components/Drawers/GlobalDiscipline/GlobalDisciplineDrawer';
import GlobalIndustryDrawer from 'app/components/Drawers/GlobalIndustry/GlobalIndustryDrawer';
import GlobalPassionDrawer from 'app/components/Drawers/GlobalPassion/GlobalPassionDrawer';
import GlobalProjectDrawer from 'app/components/Drawers/GlobalProject/GlobalProjectDrawer';
import GlobalSkillDrawer from 'app/components/Drawers/GlobalSkill/GlobalSkillDrawer';
import { useResourceClick } from 'app/components/Resources/hooks/useResourceClick';
import {
  resolveResourceClickForMode,
  ResourceClickMode,
  resourceClickLinkProps,
} from 'app/components/Resources/resourceClickBehavior';
import {
  BookIcon,
  CubeIcon,
  MinimalTrendingIcon,
  ResourceIcon,
  OpenInNewIcon,
  WorldGlobe,
} from 'app/components/shared/icons';
import Calendar from 'app/components/shared/icons/calendar';
import CommunitiesIcon from 'app/components/shared/icons/communities-icon';
import LightningIcon from 'app/components/shared/icons/lightning-icon';
import PersonIcon from 'app/components/shared/icons/person-icon';
import TrendingIcon from 'app/components/shared/icons/trending';
import { User } from 'interfaces';
import { userDataSelector } from 'store/reducers/user-reducer';
import { ThemeProvider } from 'styled-components';

import PersonAvatar from '../PersonAvatar';
import SideDrawer from '../SideDrawer';
import { PillContainer, PillText, PillWrapper } from './styled';
import { theme } from './theme';

export type DataTypePillType =
  | 'industry'
  | 'client'
  | 'company'
  | 'city'
  | 'project'
  | 'passion'
  | 'skill'
  | 'community'
  | 'locations'
  | 'country'
  | 'language'
  | 'category'
  | 'category_secondary'
  | 'person'
  | 'convening'
  | 'article'
  | 'tool'
  | 'resource'
  | 'discipline'
  | 'levels'
  | 'tenure'
  | 'work_experience'
  | 'role'
  | 'location'
  | 'level'
  | 'project_category'
  | 'more';

interface DataTypePillProps {
  type: DataTypePillType;
  title: string;
  slug?: string;
  clickable?: boolean;
  shared?: boolean;
  onClick?: () => void;
  themeMode?: 'light' | 'dark' | 'high-contrast' | 'high-contrast-light';
  behavioralFunction?: () => void;
  style?: React.CSSProperties;
  resource_type_name?: string;
  open_in_modal?: boolean;
  link?: string;
  resourceClickMode?: ResourceClickMode;
  onResourceClick?: () => void;
}

const ICON_WIDTH = 13;
const ICON_HEIGHT = 13;

export const DATATYPE_PILL_ICONS: Record<DataTypePillType, JSX.Element | null> = {
  company: <CompanyIcon fill={'#5478A1'} width={ICON_WIDTH} height={ICON_HEIGHT} />,
  client: <ClientIcon fill={'#104D5B'} width={ICON_WIDTH} height={ICON_HEIGHT} />,
  project: <ProjectIcon fill={'#392B62'} width={ICON_WIDTH} height={ICON_HEIGHT} />,
  industry: <IndustryIcon fill={'#8DA76C'} width={ICON_WIDTH} height={ICON_HEIGHT} />,
  skill: <SkillIcon fill={'#EB823D'} width={ICON_WIDTH} height={ICON_HEIGHT} />,
  passion: <PassionIcon fill={'#D47563'} width={ICON_WIDTH} height={ICON_HEIGHT} />,
  community: <CommunitiesIcon fill={'#A26565'} width={ICON_WIDTH} height={ICON_HEIGHT} />,
  person: <PersonIcon fill={'rgba(255, 194, 41, 1)'} width={ICON_WIDTH} height={ICON_HEIGHT} />,
  locations: <WorldGlobe fill={'rgba(255, 194, 41, 1)'} width={ICON_WIDTH} height={ICON_HEIGHT} />,
  location: <WorldGlobe fill={'rgba(255, 194, 41, 1)'} width={ICON_WIDTH} height={ICON_HEIGHT} />,
  language: <PeopleIcon fill={'rgba(255, 194, 41, 1)'} width={ICON_WIDTH} height={ICON_HEIGHT} />,
  city: <WorldGlobe fill={'rgba(255, 194, 41, 1)'} width={ICON_WIDTH} height={ICON_HEIGHT} />,
  country: <WorldGlobe fill={'rgba(255, 194, 41, 1)'} width={ICON_WIDTH} height={ICON_HEIGHT} />,
  category: null,
  category_secondary: null,
  more: null,
  convening: <Calendar fill={'#A26565'} width={ICON_WIDTH} height={ICON_HEIGHT} />,
  article: <BookIcon fill={'var(--color-primary)'} width={ICON_WIDTH} height={ICON_HEIGHT} />,
  tool: <LightningIcon fill={'var(--color-primary)'} width={ICON_WIDTH} height={ICON_HEIGHT} />,
  resource: <ResourceIcon fill={'var(--color-primary)'} width={ICON_WIDTH} height={ICON_HEIGHT} />,
  discipline: <DisciplineIcon fill={'#5478A1'} width={ICON_WIDTH} height={ICON_HEIGHT} />,
  levels: <DisciplineIcon fill={'#5478A1'} width={ICON_WIDTH} height={ICON_HEIGHT} />,
  level: <DisciplineIcon fill={'#5478A1'} width={ICON_WIDTH} height={ICON_HEIGHT} />,
  tenure: <CubeIcon fill="rgba(23, 28, 51, 0.5)" width={ICON_WIDTH} height={ICON_HEIGHT} />,
  work_experience: <MinimalTrendingIcon width={ICON_WIDTH} height={ICON_HEIGHT} />,
  role: <DisciplineIcon fill={'rgba(255, 194, 41, 1)'} width={ICON_WIDTH} height={ICON_HEIGHT} />,
  project_category: <ProjectIcon fill={'#392B62'} width={ICON_WIDTH} height={ICON_HEIGHT} />,
};

const DataTypePill = React.forwardRef<HTMLDivElement, DataTypePillProps>(
  (
    {
      type,
      title,
      slug,
      clickable = true,
      shared = false,
      onClick = () => {},
      themeMode = 'light',
      behavioralFunction,
      style,
      open_in_modal,
      resource_type_name,
      link,
      resourceClickMode = 'default',
      onResourceClick,
    },
    ref
  ) => {
    const { push } = useHistory();
    const [showDrawer, setShowDrawer] = useState<boolean>(false);
    const userSelector: User = useSelector(userDataSelector);
    const handleResourceClick = useResourceClick(resourceClickMode);
    const resourceClickDecision =
      type === 'resource' && slug
        ? resolveResourceClickForMode(
            {
              slug,
              open_in_modal,
              resource_format_name: resource_type_name,
              link,
            },
            resourceClickMode
          )
        : null;

    const handleNonDrawerTypesClick = (event?: React.MouseEvent<HTMLElement>) => {
      if (type === 'community') {
        return push(`/community/${slug}`);
      }

      if (type === 'convening') {
        return push(`/convening/${slug}`);
      }

      if (type === 'article') {
        return push(`/articles/${slug}`);
      }

      if (type === 'tool') {
        return push(`/tools/${slug}`);
      }

      if (type === 'resource') {
        const result = handleResourceClick(
          {
            open_in_modal,
            link,
            resource_format_name: resource_type_name,
            slug,
          },
          event
        );

        onResourceClick?.();
        return result;
      }
    };

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      if (clickable) {
        if (behavioralFunction) {
          behavioralFunction();
        }
        if (slug) {
          if (['community', 'convening', 'article', 'tool', 'resource'].includes(type)) {
            handleNonDrawerTypesClick(event);
          } else {
            setShowDrawer(true);
          }
        } else if (onClick) {
          onClick();
        }
      }
    };

    return (
      <ThemeProvider theme={theme[themeMode]}>
        <PillWrapper ref={ref} style={style}>
          <PillContainer
            type={type}
            as={resourceClickDecision && clickable ? 'a' : undefined}
            {...(resourceClickDecision && clickable
              ? resourceClickLinkProps(resourceClickDecision)
              : {})}
            clickable={clickable}
            onClick={handleClick}
            svgMinWidth={ICON_WIDTH}
          >
            {DATATYPE_PILL_ICONS[type]
              ? React.cloneElement(DATATYPE_PILL_ICONS[type], { fill: theme[themeMode].icon[type] })
              : null}
            <PillText type={type}>{title}</PillText>
            {resourceClickDecision?.opensInNewTab ? (
              <OpenInNewIcon
                width={12}
                height={12}
                fill={theme[themeMode].text[type]}
                aria-label="Opens in a new tab"
              />
            ) : null}
          </PillContainer>
          {shared ? (
            <PersonAvatar
              name={userSelector.first_name}
              lastName={userSelector.last_name}
              avatar={userSelector.profile_image}
              size={22}
              highlighted
            />
          ) : null}
          {showDrawer ? (
            <SideDrawer closeDrawer={() => setShowDrawer(false)} withClose>
              <>
                {type === 'company' && <GlobalCompanyDrawer slug={slug} />}
                {type === 'industry' && <GlobalIndustryDrawer slug={slug} />}
                {type === 'client' && <GlobalClientDrawer slug={slug} />}
                {type === 'passion' && <GlobalPassionDrawer slug={slug} />}
                {type === 'skill' && <GlobalSkillDrawer slug={slug} />}
                {type === 'project' && <GlobalProjectDrawer slug={slug} />}
                {type === 'discipline' && <GlobalDisciplineDrawer slug={slug} />}
              </>
            </SideDrawer>
          ) : null}
        </PillWrapper>
      </ThemeProvider>
    );
  }
);

export default DataTypePill;
